import { useState } from 'react';

export default function SettingsPage() {
  const [defaultCarrier, setDefaultCarrier] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [autoSchedulePickup, setAutoSchedulePickup] = useState(false);
  const [pickupTimeWindow, setPickupTimeWindow] = useState('8am - 5pm');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // In a real implementation, this would save to the API
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Shipping Settings</h1>
        <p className="text-gray-400 mt-1">Configure shipping portal preferences</p>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Default Settings</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Carrier
            </label>
            <select
              value={defaultCarrier}
              onChange={(e) => setDefaultCarrier(e.target.value)}
              className="w-full max-w-md px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a carrier...</option>
              <option value="fedex_freight">FedEx Freight</option>
              <option value="ups_freight">UPS Freight</option>
              <option value="old_dominion">Old Dominion</option>
              <option value="estes">Estes Express</option>
              <option value="xpo">XPO Logistics</option>
              <option value="saia">SAIA</option>
              <option value="yrc">YRC Freight</option>
            </select>
            <p className="text-gray-500 text-sm mt-1">
              Pre-selected carrier when booking shipments
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Pickup Time Window
            </label>
            <input
              type="text"
              value={pickupTimeWindow}
              onChange={(e) => setPickupTimeWindow(e.target.value)}
              className="w-full max-w-md px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., 8am - 5pm"
            />
            <p className="text-gray-500 text-sm mt-1">
              Default time window for scheduling pickups
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Notifications</h2>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <span className="text-white">Email notifications to dealers</span>
              <p className="text-gray-500 text-sm">
                Send automatic emails when orders are shipped and delivered
              </p>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Automation</h2>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoSchedulePickup}
              onChange={(e) => setAutoSchedulePickup(e.target.checked)}
              className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <span className="text-white">Auto-schedule pickup after booking</span>
              <p className="text-gray-500 text-sm">
                Automatically schedule pickup for the next business day when a shipment is booked
              </p>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Label Templates</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Shipping Label</h3>
              <span className="text-xs text-gray-400">4x6 ZPL</span>
            </div>
            <p className="text-gray-400 text-sm mb-3">Standard shipping label with barcode</p>
            <button className="text-indigo-400 text-sm hover:text-indigo-300">
              Edit Template
            </button>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Packing Slip</h3>
              <span className="text-xs text-gray-400">8.5x11 PDF</span>
            </div>
            <p className="text-gray-400 text-sm mb-3">Order summary and item list</p>
            <button className="text-indigo-400 text-sm hover:text-indigo-300">
              Edit Template
            </button>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Bill of Lading</h3>
              <span className="text-xs text-gray-400">8.5x11 PDF</span>
            </div>
            <p className="text-gray-400 text-sm mb-3">Standard BOL for LTL freight</p>
            <button className="text-indigo-400 text-sm hover:text-indigo-300">
              Edit Template
            </button>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Delivery Receipt</h3>
              <span className="text-xs text-gray-400">8.5x11 PDF</span>
            </div>
            <p className="text-gray-400 text-sm mb-3">Customer signature receipt</p>
            <button className="text-indigo-400 text-sm hover:text-indigo-300">
              Edit Template
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saved && (
          <span className="text-green-400 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
